import { Controller, useFormContext, useWatch } from 'react-hook-form';
import { Radio, RadioGroup, SelectItem, TmdxSelect, TmdxTextarea } from '@ds';
import { FormSection } from '@features/components/CaseFormBlocks/components';
import { DonorType, NeuroStatus } from '@/api/apis/case';
import { DonorFormRow } from './DonorFormRow';
import { DonorProfileFormValues } from '../types';

const NEURO_STATUS_OPTIONS: { value: NeuroStatus; label: string }[] = [
  { value: NeuroStatus.BrainDead, label: 'Brain Dead' },
  { value: NeuroStatus.BrainIsNotDead, label: 'Not Brain Dead (DCD)' },
  { value: NeuroStatus.PendingBrainDeathDeclaration, label: 'Pending Brain Death Declaration' },
];

type ParametersSectionProps = {
  /** DCD-only Neuro Status. Omit to always show it. */
  donorType?: DonorType | null;
};

export const ParametersSection = ({ donorType }: ParametersSectionProps) => {
  const { control } = useFormContext<DonorProfileFormValues>();

  const showNeuroStatus = donorType === DonorType.DCD;
  const [vasopressors, sustainedHypotension] = useWatch({ control, name: ['vasopressors', 'sustainedHypotension'] });

  return (
    <FormSection title="Parameters">
      {showNeuroStatus && (
        <DonorFormRow title="Neuro Status">
          <div className="w-[332px]">
            <Controller
              name="neuroStatus"
              control={control}
              render={({ field: { value, ...field }, fieldState: { invalid, error } }) => (
                <TmdxSelect
                  placeholder="Select"
                  aria-label="Neuro status"
                  selectedKeys={value ? [value] : []}
                  isInvalid={invalid}
                  errorMessage={error?.message}
                  {...field}
                >
                  {NEURO_STATUS_OPTIONS.map((opt) => (
                    <SelectItem key={opt.value}>{opt.label}</SelectItem>
                  ))}
                </TmdxSelect>
              )}
            />
          </div>
        </DonorFormRow>
      )}

      <DonorFormRow title="Vasopressors">
        <Controller
          name="vasopressors"
          control={control}
          render={({ field: { value, onChange, ...field }, fieldState: { invalid, error } }) => (
            <RadioGroup
              orientation="horizontal"
              value={value === null || value === undefined ? '' : String(value)}
              isInvalid={invalid}
              errorMessage={error?.message}
              onValueChange={(v) => onChange(v === 'true')}
              {...field}
            >
              <Radio className="w-[100px] max-w-none" classNames={{ label: 'pl-1' }} value="true" size="lg">
                Yes
              </Radio>
              <Radio className="w-[100px] max-w-none" classNames={{ label: 'pl-1' }} value="false" size="lg">
                No
              </Radio>
            </RadioGroup>
          )}
        />
      </DonorFormRow>

      {vasopressors && (
        <DonorFormRow title="Vasopressors Details" multiRow>
          <Controller
            name="vasopressorsDetails"
            control={control}
            render={({ field, fieldState: { invalid, error } }) => (
              <TmdxTextarea
                aria-label="Vasopressors details"
                maxLength={512}
                isInvalid={invalid}
                errorMessage={error?.message}
                {...field}
                value={field.value ?? ''}
              />
            )}
          />
        </DonorFormRow>
      )}

      <DonorFormRow title="Sustained Hypotension">
        <Controller
          name="sustainedHypotension"
          control={control}
          render={({ field: { value, onChange, ...field }, fieldState: { invalid, error } }) => (
            <RadioGroup
              orientation="horizontal"
              value={value === null || value === undefined ? '' : String(value)}
              isInvalid={invalid}
              errorMessage={error?.message}
              onValueChange={(v) => onChange(v === 'true')}
              {...field}
            >
              <Radio className="w-[100px] max-w-none" classNames={{ label: 'pl-1' }} value="true" size="lg">
                Yes
              </Radio>
              <Radio className="w-[100px] max-w-none" classNames={{ label: 'pl-1' }} value="false" size="lg">
                No
              </Radio>
            </RadioGroup>
          )}
        />
      </DonorFormRow>

      {sustainedHypotension && (
        <DonorFormRow title="Sustained Hypotension Details" multiRow>
          <Controller
            name="sustainedHypotensionDetails"
            control={control}
            render={({ field, fieldState: { invalid, error } }) => (
              <TmdxTextarea
                aria-label="Sustained hypotension details"
                maxLength={512}
                isInvalid={invalid}
                errorMessage={error?.message}
                {...field}
                value={field.value ?? ''}
              />
            )}
          />
        </DonorFormRow>
      )}
    </FormSection>
  );
};
